import React, { useState } from 'react';
import { X, Loader2, AlertTriangle, Facebook, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { selectFacebookPage } from '../../../api/socialMedia';

/**
 * Modal for selecting a Facebook Page after OAuth callback
 * Facebook OAuth returns a user token, but posting requires a page token.
 * This modal lets users choose which of their pages to connect.
 */
const FacebookPagePickerModal = ({
    isOpen,
    pages = [],
    oauthState,
    onSuccess,
    onCancel
}) => {
    const [loading, setLoading] = useState(null);
    const [error, setError] = useState(null);

    const handleSelectPage = async (page) => {
        console.log('[FacebookPagePicker] Selecting page:', page.name, page.id);
        setLoading(page.id);
        setError(null);

        try {
            // Backend expects snake_case in request body
            const response = await selectFacebookPage({
                oauth_state: oauthState,
                page_id: page.id,
                page_access_token: page.accessToken,
            });

            console.log('[FacebookPagePicker] Page selection successful:', response);
            onSuccess(response);
        } catch (err) {
            console.error('[FacebookPagePicker] Failed to connect Facebook page:', err);

            // Handle specific error cases
            if (err.message.includes('expired') || err.message.includes('Invalid')) {
                setError('Session expired. Please try connecting again.');
            } else if (err.message.includes('already used')) {
                setError('This selection was already processed. Please try connecting again.');
            } else {
                setError(err.message || 'Failed to connect page. Please try again.');
            }
            setLoading(null);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
                onClick={onCancel}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full max-w-md max-h-[80vh] bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden flex flex-col"
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                                <Facebook className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                                    Select a Facebook Page
                                </h2>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    Choose which page you want to connect
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onCancel}
                            className="p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Error Message */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="px-6 pt-4"
                            >
                                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Pages List */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-3">
                        {pages.length === 0 ? (
                            <div className="text-center py-8">
                                <Facebook className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                                <p className="text-slate-600 dark:text-slate-400">
                                    No Facebook Pages found
                                </p>
                                <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
                                    You need to create a Facebook Page first
                                </p>
                            </div>
                        ) : (
                            pages.map((page) => (
                                <button
                                    key={page.id}
                                    onClick={() => handleSelectPage(page)}
                                    disabled={loading !== null}
                                    className={`w-full p-4 rounded-xl border transition-all flex items-center gap-4 text-left ${
                                        loading === page.id
                                            ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20'
                                            : loading !== null
                                            ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 opacity-50 cursor-not-allowed'
                                            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                                    }`}
                                >
                                    {/* Page Avatar */}
                                    <div className="relative flex-shrink-0">
                                        {page.pictureUrl ? (
                                            <img
                                                src={page.pictureUrl}
                                                alt={page.name}
                                                className="w-12 h-12 rounded-full border-2 border-white dark:border-slate-700 shadow"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                                <Facebook className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                                            </div>
                                        )}
                                        {loading === page.id && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-slate-800/80 rounded-full">
                                                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Page Info */}
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-slate-900 dark:text-white truncate">
                                            {page.name}
                                        </p>
                                        <div className="flex items-center gap-2 mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                                            {page.fanCount !== null && page.fanCount !== undefined && (
                                                <span className="flex items-center gap-1">
                                                    <Users className="w-3.5 h-3.5" />
                                                    {page.fanCount.toLocaleString()} followers
                                                </span>
                                            )}
                                            {page.category && (
                                                <>
                                                    {page.fanCount !== null && <span>•</span>}
                                                    <span className="truncate">{page.category}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Selection indicator */}
                                    <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 transition-colors ${
                                        loading === page.id
                                            ? 'border-blue-600 bg-blue-600'
                                            : 'border-slate-300 dark:border-slate-600'
                                    }`}>
                                        {loading === page.id && (
                                            <motion.div
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                className="w-full h-full rounded-full bg-blue-600"
                                            />
                                        )}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                        <p className="text-xs text-slate-500 dark:text-slate-500">
                            You can connect additional pages later
                        </p>
                        <button
                            onClick={onCancel}
                            disabled={loading !== null}
                            className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default FacebookPagePickerModal;
